"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const token_1 = require("./token");
const node_1 = require("./node");
const constant_1 = require("./node/expression/constant");
const syntax_1 = require("./error/syntax");
const lexer_1 = require("./lexer");
const name_1 = require("./node/expression/name");
const concat_1 = require("./node/expression/binary/concat");
const get_attr_1 = require("./node/expression/get-attr");
const method_call_1 = require("./node/expression/method-call");
const array_1 = require("./node/expression/array");
const conditional_1 = require("./node/expression/conditional");
const template_1 = require("./template");
const assign_name_1 = require("./node/expression/assign-name");
const parent_1 = require("./node/expression/parent");
const block_reference_1 = require("./node/expression/block-reference");
const hash_1 = require("./node/expression/hash");
const not_1 = require("./node/expression/unary/not");
const push_1 = require("./helper/push");
const arrow_function_1 = require("./node/expression/arrow-function");
/**
 * Parses expressions.
 *
 * This parser implements a "Precedence climbing" algorithm.
 *
 * @see https://www.engr.mun.ca/~theo/Misc/exp_parsing.htm
 * @see https://en.wikipedia.org/wiki/Operator-precedence_parser
 *
 * @author Fabien Potencier <fabien@symfony.com>
 * @author Eric MORAND <eric.morand@gmail.com>
 *
 * @internal
 */
class TwingExpressionParser {
    constructor(parser, env) {
        this.parser = parser;
        this.env = env;
        this.unaryOperators = env.getUnaryOperators();
        this.binaryOperators = env.getBinaryOperators();
    }
    parseExpression(precedence = 0, allowArrow = false) {
        if (allowArrow) {
            let arrow = this.parseArrow();
            if (arrow) {
                return arrow;
            }
        }
        let expr = this.getPrimary();
        let token = this.parser.getCurrentToken();
        while (this.isBinary(token) && this.binaryOperators.get(token.getValue()).precedence >= precedence) {
            let op = this.binaryOperators.get(token.getValue());
            this.parser.getStream().next();
            if (token.getValue() === 'is not') {
                expr = this.parseNotTestExpression(expr);
            }
            else if (token.getValue() === 'is') {
                expr = this.parseTestExpression(expr);
            }
            else if (op.callable) {
                expr = op.callable.call(this.parser, expr);
            }
            else {
                let expr1 = this.parseExpression(op.associativity === TwingExpressionParser.OPERATOR_LEFT ? op.precedence + 1 : op.precedence);
                let opFactory = op.factory;
                expr = opFactory(expr, expr1, token.getLine());
            }
            token = this.parser.getCurrentToken();
        }
        if (precedence === 0) {
            return this.parseConditionalExpression(expr);
        }
        return expr;
    }
    /**
     * @return TwingNodeExpressionArrowFunction|null
     */
    parseArrow() {
        let stream = this.parser.getStream();
        let token;
        let line;
        let column;
        let names;
        // short array syntax (one argument, no parentheses)?
        if (stream.look(1).test(token_1.TwingToken.ARROW_TYPE)) {
            line = stream.getCurrent().getLine();
            column = stream.getCurrent().getColumn();
            token = stream.expect(token_1.TwingToken.NAME_TYPE);
            names = new Map([[0, new assign_name_1.TwingNodeExpressionAssignName(token.getValue(), token.getLine(), token.getColumn())]]);
            stream.expect(token_1.TwingToken.ARROW_TYPE);
            return new arrow_function_1.TwingNodeExpressionArrowFunction(this.parseExpression(0), new node_1.TwingNode(names), line, column);
        }
        // first, determine if we are parsing an arrow function by finding => (long form)
        let i = 0;
        if (!stream.look(i).test(token_1.TwingToken.PUNCTUATION_TYPE, '(')) {
            return null;
        }
        ++i;
        while (true) {
            // variable name
            ++i;
            if (!stream.look(i).test(token_1.TwingToken.PUNCTUATION_TYPE, ',')) {
                break;
            }
            ++i;
        }
        if (!stream.look(i).test(token_1.TwingToken.PUNCTUATION_TYPE, ')')) {
            return null;
        }
        ++i;
        if (!stream.look(i).test(token_1.TwingToken.ARROW_TYPE)) {
            return null;
        }
        // yes, let's parse it properly
        token = stream.expect(token_1.TwingToken.PUNCTUATION_TYPE, '(');
        line = token.getLine();
        column = token.getColumn();
        names = new Map();
        i = 0;
        while (true) {
            token = this.parser.getCurrentToken();
            if (!token.test(token_1.TwingToken.NAME_TYPE)) {
                throw new syntax_1.TwingErrorSyntax(`Unexpected token "${token_1.TwingToken.typeToEnglish(token.getType())}" of value "${token.getValue()}".`, token.getLine(), stream.getSourceContext());
            }
            names.set(i++, new assign_name_1.TwingNodeExpressionAssignName(token.getValue(), token.getLine(), token.getColumn()));
            stream.next();
            if (!stream.nextIf(token_1.TwingToken.PUNCTUATION_TYPE, ',')) {
                break;
            }
        }
        stream.expect(token_1.TwingToken.PUNCTUATION_TYPE, ')');
        stream.expect(token_1.TwingToken.ARROW_TYPE);
        return new arrow_function_1.TwingNodeExpressionArrowFunction(this.parseExpression(0), new node_1.TwingNode(names), line, column);
    }
    getPrimary() {
        let token = this.parser.getCurrentToken();
        if (this.isUnary(token)) {
            let operator = this.unaryOperators.get(token.getValue());
            this.parser.getStream().next();
            let expr = this.parseExpression(operator.precedence);
            return this.parsePostfixExpression(operator.factory(expr, token.getLine()));
        }
        else if (token.test(token_1.TwingToken.PUNCTUATION_TYPE, '(')) {
            this.parser.getStream().next();
            let expr = this.parseExpression();
            this.parser.getStream().expect(token_1.TwingToken.PUNCTUATION_TYPE, ')', 'An opened parenthesis is not properly closed');
            return this.parsePostfixExpression(expr);
        }
        return this.parsePrimaryExpression();
    }
    parsePrimaryExpression() {
        let token = this.parser.getCurrentToken();
        let node;
        switch (token.getType()) {
            case token_1.TwingToken.NAME_TYPE:
                this.parser.getStream().next();
                switch (token.getValue()) {
                    case 'true':
                    case 'TRUE':
                        node = new constant_1.TwingNodeExpressionConstant(true, token.getLine(), token.getColumn());
                        break;
                    case 'false':
                    case 'FALSE':
                        node = new constant_1.TwingNodeExpressionConstant(false, token.getLine(), token.getColumn());
                        break;
                    case 'none':
                    case 'NONE':
                    case 'null':
                    case 'NULL':
                        node = new constant_1.TwingNodeExpressionConstant(null, token.getLine(), token.getColumn());
                        break;
                    default:
                        if ('(' === this.parser.getCurrentToken().getValue()) {
                            node = this.getFunctionNode(token.getValue(), token.getLine(), token.getColumn());
                        }
                        else {
                            node = new name_1.TwingNodeExpressionName(token.getValue(), token.getLine(), token.getColumn());
                        }
                }
                break;
            case token_1.TwingToken.NUMBER_TYPE:
                this.parser.getStream().next();
                node = new constant_1.TwingNodeExpressionConstant(token.getValue(), token.getLine(), token.getColumn());
                break;
            case token_1.TwingToken.STRING_TYPE:
            case token_1.TwingToken.INTERPOLATION_START_TYPE:
                node = this.parseStringExpression();
                break;
            case token_1.TwingToken.OPERATOR_TYPE:
                let match = lexer_1.TwingLexer.REGEX_NAME.exec(token.getValue());
                if (match !== null && match[0] === token.getValue()) {
                    // in this context, string operators are variable names
                    this.parser.getStream().next();
                    node = new name_1.TwingNodeExpressionName(token.getValue(), token.getLine(), token.getColumn());
                    break;
                }
                else if (this.unaryOperators.has(token.getValue())) {
                    let op = this.unaryOperators.get(token.getValue());
                    //
                    // ref = new ReflectionClass(class);
                    // negClass = 'Twig_Node_Expression_Unary_Neg';
                    // posClass = 'Twig_Node_Expression_Unary_Pos';
                    //
                    // if (!(in_array(ref.getTemplateName(), array(negClass, posClass)) || ref.isSubclassOf(negClass) || ref.isSubclassOf(posClass))) {
                    //     throw new Twig_Error_Syntax(sprintf('Unexpected unary operator "%s".', token.getValue()), token.getLine(), this.parser.getStream().getSourceContext());
                    // }
                    //
                    this.parser.getStream().next();
                    let expr = this.parsePrimaryExpression();
                    node = op.factory(expr, token.getLine());
                    break;
                }
            default:
                if (token.test(token_1.TwingToken.PUNCTUATION_TYPE, '[')) {
                    node = this.parseArrayExpression();
                }
                else if (token.test(token_1.TwingToken.PUNCTUATION_TYPE, '{')) {
                    node = this.parseHashExpression();
                }
                else if (token.test(token_1.TwingToken.OPERATOR_TYPE, '=') && (this.parser.getStream().look(-1).getValue() === '==' || this.parser.getStream().look(-1).getValue() === '!=')) {
                    throw new syntax_1.TwingErrorSyntax(`Unexpected operator of value "${token.getValue()}". Did you try to use "===" or "!==" for strict comparison? Use "is same as(value)" instead.`, token.getLine(), this.parser.getStream().getSourceContext());
                }
                else {
                    throw new syntax_1.TwingErrorSyntax(`Unexpected token "${token_1.TwingToken.typeToEnglish(token.getType())}" of value "${token.getValue()}".`, token.getLine(), this.parser.getStream().getSourceContext());
                }
        }
        return this.parsePostfixExpression(node);
    }
    getFunctionNode(name, line, column) {
        switch (name) {
            case 'parent':
                this.parseArguments();
                if (!this.parser.getBlockStack().length) {
                    throw new syntax_1.TwingErrorSyntax('Calling "parent" outside a block is forbidden.', line, this.parser.getStream().getSourceContext());
                }
                if (!this.parser.getParent() && !this.parser.hasTraits()) {
                    throw new syntax_1.TwingErrorSyntax('Calling "parent" on a template that does not extend nor "use" another template is forbidden.', line, this.parser.getStream().getSourceContext());
                }
                return new parent_1.TwingNodeExpressionParent(this.parser.peekBlockStack(), line);
            case 'block':
                let blockArgs = this.parseArguments();
                if (blockArgs.getNodes().size < 1) {
                    throw new syntax_1.TwingErrorSyntax('The "block" function takes one argument (the block name).', line, this.parser.getStream().getSourceContext());
                }
                return new block_reference_1.TwingNodeExpressionBlockReference(blockArgs.getNode(0), blockArgs.getNodes().size > 1 ? blockArgs.getNode(1) : null, line, column);
            case 'attribute':
                let attributeArgs = this.parseArguments();
                if (attributeArgs.getNodes().size < 2) {
                    throw new syntax_1.TwingErrorSyntax('The "attribute" function takes at least two arguments (the variable and the attributes).', line, this.parser.getStream().getSourceContext());
                }
                return new get_attr_1.TwingNodeExpressionGetAttr(attributeArgs.getNode(0), attributeArgs.getNode(1), attributeArgs.getNodes().size > 2 ? attributeArgs.getNode(2) : null, template_1.TwingTemplate.ANY_CALL, line, column);
            default:
                let alias = this.parser.getImportedSymbol('function', name);
                if (alias) {
                    let functionArguments = new array_1.TwingNodeExpressionArray(new Map(), line, column);
                    this.parseArguments().getNodes().forEach(function (n, name) {
                        functionArguments.addElement(n);
                    });
                    let node = new method_call_1.TwingNodeExpressionMethodCall(alias.node, alias.name, functionArguments, line, column);
                    node.setAttribute('safe', true);
                    return node;
                }
                let aliasArguments = this.parseArguments(true);
                let aliasFactory = this.getFunctionExpressionFactory(name, line, column);
                return aliasFactory(name, aliasArguments, line, column);
        }
    }
    parseStringExpression() {
        let stream = this.parser.getStream();
        let nodes = [];
        // a string cannot be followed by another string in a single expression
        let nextCanBeString = true;
        let token;
        while (true) {
            if (nextCanBeString && (token = stream.nextIf(token_1.TwingToken.STRING_TYPE))) {
                nodes.push(new constant_1.TwingNodeExpressionConstant(token.getValue(), token.getLine(), token.getColumn()));
                nextCanBeString = false;
            }
            else if (stream.nextIf(token_1.TwingToken.INTERPOLATION_START_TYPE)) {
                nodes.push(this.parseExpression());
                stream.expect(token_1.TwingToken.INTERPOLATION_END_TYPE);
                nextCanBeString = true;
            }
            else {
                break;
            }
        }
        let expr = nodes.shift();
        nodes.forEach(function (node) {
            expr = new concat_1.TwingNodeExpressionBinaryConcat(expr, node, node.getTemplateLine(), node.getTemplateColumn());
        });
        return expr;
    }
    parseArrayExpression() {
        let stream = this.parser.getStream();
        stream.expect(token_1.TwingToken.PUNCTUATION_TYPE, '[', 'An array element was expected');
        let node = new array_1.TwingNodeExpressionArray(new Map(), stream.getCurrent().getLine(), stream.getCurrent().getColumn());
        let first = true;
        while (!stream.test(token_1.TwingToken.PUNCTUATION_TYPE, ']')) {
            if (!first) {
                stream.expect(token_1.TwingToken.PUNCTUATION_TYPE, ',', 'An array element must be followed by a comma');
                // trailing ,?
                if (stream.test(token_1.TwingToken.PUNCTUATION_TYPE, ']')) {
                    break;
                }
            }
            first = false;
            node.addElement(this.parseExpression());
        }
        stream.expect(token_1.TwingToken.PUNCTUATION_TYPE, ']', 'An opened array is not properly closed');
        return node;
    }
    parseHashExpression() {
        let stream = this.parser.getStream();
        stream.expect(token_1.TwingToken.PUNCTUATION_TYPE, '{', 'A hash element was expected');
        let node = new hash_1.TwingNodeExpressionHash(new Map(), stream.getCurrent().getLine(), stream.getCurrent().getColumn());
        let first = true;
        while (!stream.test(token_1.TwingToken.PUNCTUATION_TYPE, '}')) {
            if (!first) {
                stream.expect(token_1.TwingToken.PUNCTUATION_TYPE, ',', 'A hash value must be followed by a comma');
                // trailing ,?
                if (stream.test(token_1.TwingToken.PUNCTUATION_TYPE, '}')) {
                    break;
                }
            }
            first = false;
            // a hash key can be:
            //
            //  * a number -- 12
            //  * a string -- 'a'
            //  * a name, which is equivalent to a string -- a
            //  * an expression, which must be enclosed in parentheses -- (1 + 2)
            let token;
            let key;
            if ((token = stream.nextIf(token_1.TwingToken.STRING_TYPE)) || (token = stream.nextIf(token_1.TwingToken.NAME_TYPE)) || (token = stream.nextIf(token_1.TwingToken.NUMBER_TYPE))) {
                key = new constant_1.TwingNodeExpressionConstant(token.getValue(), token.getLine(), token.getColumn());
            }
            else if (stream.test(token_1.TwingToken.PUNCTUATION_TYPE, '(')) {
                key = this.parseExpression();
            }
            else {
                let current = stream.getCurrent();
                throw new syntax_1.TwingErrorSyntax(`A hash key must be a quoted string, a number, a name, or an expression enclosed in parentheses (unexpected token "${token_1.TwingToken.typeToEnglish(current.getType())}" of value "${current.getValue()}".`, current.getLine(), stream.getSourceContext());
            }
            stream.expect(token_1.TwingToken.PUNCTUATION_TYPE, ':', 'A hash key must be followed by a colon (:)');
            let value = this.parseExpression();
            node.addElement(value, key);
        }
        stream.expect(token_1.TwingToken.PUNCTUATION_TYPE, '}', 'An opened hash is not properly closed');
        return node;
    }
    parseSubscriptExpression(node) {
        let stream = this.parser.getStream();
        let token = stream.next();
        let lineno = token.getLine();
        let columnno = token.getColumn();
        let arguments_ = new array_1.TwingNodeExpressionArray(new Map(), lineno, columnno);
        let arg;
        let type = template_1.TwingTemplate.ANY_CALL;
        if (token.getValue() === '.') {
            token = stream.next();
            let match = lexer_1.TwingLexer.REGEX_NAME.exec(token.getValue());
            if ((token.getType() === token_1.TwingToken.NAME_TYPE) || (token.getType() === token_1.TwingToken.NUMBER_TYPE) || (token.getType() === token_1.TwingToken.OPERATOR_TYPE && (match !== null))) {
                arg = new constant_1.TwingNodeExpressionConstant(token.getValue(), lineno, columnno);
                if (stream.test(token_1.TwingToken.PUNCTUATION_TYPE, '(')) {
                    type = template_1.TwingTemplate.METHOD_CALL;
                    let node = this.parseArguments();
                    node.getNodes().forEach(function (n, k) {
                        arguments_.addElement(n);
                    });
                }
            }
            else {
                throw new syntax_1.TwingErrorSyntax('Expected name or number.', lineno, stream.getSourceContext());
            }
            if ((node.getType() === node_1.TwingNodeType.EXPRESSION_NAME) && this.parser.getImportedSymbol('template', node.getAttribute('name'))) {
                const varValidator = require('var-validator');
                let name = arg.getAttribute('value');
                if (!varValidator.isValid(name)) {
                    name = Buffer.from(name).toString('hex');
                }
                node = new method_call_1.TwingNodeExpressionMethodCall(node, 'macro_' + name, arguments_, lineno, columnno);
                node.setAttribute('safe', true);
                return node;
            }
        }
        else {
            type = template_1.TwingTemplate.ARRAY_CALL;
            // slice?
            let slice = false;
            if (stream.test(token_1.TwingToken.PUNCTUATION_TYPE, ':')) {
                slice = true;
                arg = new constant_1.TwingNodeExpressionConstant(0, token.getLine(), token.getColumn());
            }
            else {
                arg = this.parseExpression();
            }
            if (stream.nextIf(token_1.TwingToken.PUNCTUATION_TYPE, ':')) {
                slice = true;
            }
            if (slice) {
                let length;
                if (stream.test(token_1.TwingToken.PUNCTUATION_TYPE, ']')) {
                    length = new constant_1.TwingNodeExpressionConstant(null, token.getLine(), token.getColumn());
                }
                else {
                    length = this.parseExpression();
                }
                let factory = this.getFilterExpressionFactory('slice', token.getLine(), token.getColumn());
                let filterArguments = new node_1.TwingNode(new Map([[0, arg], [1, length]]));
                let filter = factory.call(this, node, new constant_1.TwingNodeExpressionConstant('slice', token.getLine(), token.getColumn()), filterArguments, token.getLine(), token.getColumn());
                stream.expect(token_1.TwingToken.PUNCTUATION_TYPE, ']');
                return filter;
            }
            stream.expect(token_1.TwingToken.PUNCTUATION_TYPE, ']');
        }
        return new get_attr_1.TwingNodeExpressionGetAttr(node, arg, arguments_, type, lineno, columnno);
    }
    parsePostfixExpression(node) {
        while (true) {
            let token = this.parser.getCurrentToken();
            if (token.getType() === token_1.TwingToken.PUNCTUATION_TYPE) {
                if ('.' == token.getValue() || '[' == token.getValue()) {
                    node = this.parseSubscriptExpression(node);
                }
                else if ('|' == token.getValue()) {
                    node = this.parseFilterExpression(node);
                }
                else {
                    break;
                }
            }
            else {
                break;
            }
        }
        return node;
    }
    parseTestExpression(node) {
        let stream = this.parser.getStream();
        let name;
        let test;
        [name, test] = this.getTest(node.getTemplateLine());
        let nodeFactory = test.getNodeFactory();
        let testArguments = null;
        if (stream.test(token_1.TwingToken.PUNCTUATION_TYPE, '(')) {
            testArguments = this.parser.getExpressionParser().parseArguments(true);
        }
        return nodeFactory.call(this, node, name, testArguments, this.parser.getCurrentToken().getLine());
    }
    parseNotTestExpression(node) {
        return new not_1.TwingNodeExpressionUnaryNot(this.parseTestExpression(node), this.parser.getCurrentToken().getLine(), this.parser.getCurrentToken().getColumn());
    }
    parseConditionalExpression(expr) {
        let expr2;
        let expr3;
        while (this.parser.getStream().nextIf(token_1.TwingToken.PUNCTUATION_TYPE, '?')) {
            if (!this.parser.getStream().nextIf(token_1.TwingToken.PUNCTUATION_TYPE, ':')) {
                expr2 = this.parseExpression();
                if (this.parser.getStream().nextIf(token_1.TwingToken.PUNCTUATION_TYPE, ':')) {
                    expr3 = this.parseExpression();
                }
                else {
                    expr3 = new constant_1.TwingNodeExpressionConstant('', this.parser.getCurrentToken().getLine(), this.parser.getCurrentToken().getColumn());
                }
            }
            else {
                expr2 = expr;
                expr3 = this.parseExpression();
            }
            expr = new conditional_1.TwingNodeExpressionConditional(expr, expr2, expr3, this.parser.getCurrentToken().getLine(), this.parser.getCurrentToken().getColumn());
        }
        return expr;
    }
    parseFilterExpression(node) {
        this.parser.getStream().next();
        return this.parseFilterExpressionRaw(node);
    }
    parseFilterExpressionRaw(node, tag = null) {
        while (true) {
            let token = this.parser.getStream().expect(token_1.TwingToken.NAME_TYPE);
            let name = new constant_1.TwingNodeExpressionConstant(token.getValue(), token.getLine(), token.getColumn());
            let methodArguments;
            if (!this.parser.getStream().test(token_1.TwingToken.PUNCTUATION_TYPE, '(')) {
                methodArguments = new node_1.TwingNode();
            }
            else {
                methodArguments = this.parseArguments(true, false, true);
            }
            let factory = this.getFilterExpressionFactory('' + name.getAttribute('value'), token.getLine(), token.getColumn());
            node = factory.call(this, node, name, methodArguments, token.getLine(), tag);
            if (!this.parser.getStream().test(token_1.TwingToken.PUNCTUATION_TYPE, '|')) {
                break;
            }
            this.parser.getStream().next();
        }
        return node;
    }
    /**
     * Parses arguments.
     *
     * @param namedArguments {boolean} Whether to allow named arguments or not
     * @param definition {boolean} Whether we are parsing arguments for a function definition
     * @param allowArrow {boolean}
     *
     * @return TwingNode
     *
     * @throws TwingErrorSyntax
     */
    parseArguments(namedArguments = false, definition = false, allowArrow = false) {
        let parsedArguments = new Map();
        let stream = this.parser.getStream();
        let value;
        let token;
        stream.expect(token_1.TwingToken.PUNCTUATION_TYPE, '(', 'A list of arguments must begin with an opening parenthesis');
        while (!stream.test(token_1.TwingToken.PUNCTUATION_TYPE, ')')) {
            if (parsedArguments.size > 0) {
                stream.expect(token_1.TwingToken.PUNCTUATION_TYPE, ',', 'Arguments must be separated by a comma');
            }
            if (definition) {
                token = stream.expect(token_1.TwingToken.NAME_TYPE, null, 'An argument must be a name');
                value = new name_1.TwingNodeExpressionName(token.getValue(), this.parser.getCurrentToken().getLine(), this.parser.getCurrentToken().getColumn());
            }
            else {
                value = this.parseExpression(0, allowArrow);
            }
            let name = null;
            if (namedArguments && (token = stream.nextIf(token_1.TwingToken.OPERATOR_TYPE, '='))) {
                if (value.getType() !== node_1.TwingNodeType.EXPRESSION_NAME) {
                    throw new syntax_1.TwingErrorSyntax(`A parameter name must be a string, "${value.constructor.name}" given.`, token.getLine(), stream.getSourceContext());
                }
                name = value.getAttribute('name');
                if (definition) {
                    value = this.parsePrimaryExpression();
                    if (!this.checkConstantExpression(value)) {
                        throw new syntax_1.TwingErrorSyntax(`A default value for an argument must be a constant (a boolean, a string, a number, or an array).`, token.getLine(), stream.getSourceContext());
                    }
                }
                else {
                    value = this.parseExpression(0, allowArrow);
                }
            }
            if (definition) {
                if (null === name) {
                    name = value.getAttribute('name');
                    value = new constant_1.TwingNodeExpressionConstant(null, this.parser.getCurrentToken().getLine(), this.parser.getCurrentToken().getColumn());
                }
                parsedArguments.set(name, value);
            }
            else {
                if (null === name) {
                    push_1.push(parsedArguments, value);
                }
                else {
                    parsedArguments.set(name, value);
                }
            }
        }
        stream.expect(token_1.TwingToken.PUNCTUATION_TYPE, ')', 'A list of arguments must be closed by a parenthesis');
        return new node_1.TwingNode(parsedArguments);
    }
    parseAssignmentExpression() {
        let stream = this.parser.getStream();
        let targets = new Map();
        while (true) {
            let token = this.parser.getCurrentToken();
            if (stream.test(token_1.TwingToken.OPERATOR_TYPE) && lexer_1.TwingLexer.REGEX_NAME.exec(token.getValue())) {
                // in this context, string operators are variable names
                this.parser.getStream().next();
            }
            else {
                stream.expect(token_1.TwingToken.NAME_TYPE, null, 'Only variables can be assigned to');
            }
            let value = token.getValue();
            if (['true', 'false', 'none', 'null'].indexOf(value.toLowerCase()) > -1) {
                throw new syntax_1.TwingErrorSyntax(`You cannot assign a value to "${value}".`, token.getLine(), stream.getSourceContext());
            }
            push_1.push(targets, new assign_name_1.TwingNodeExpressionAssignName(value, token.getLine(), token.getColumn()));
            if (!stream.nextIf(token_1.TwingToken.PUNCTUATION_TYPE, ',')) {
                break;
            }
        }
        return new node_1.TwingNode(targets);
    }
    parseMultitargetExpression() {
        let targets = new Map();
        while (true) {
            push_1.push(targets, this.parseExpression());
            if (!this.parser.getStream().nextIf(token_1.TwingToken.PUNCTUATION_TYPE, ',')) {
                break;
            }
        }
        return new node_1.TwingNode(targets);
    }
    // checks that the node only contains "constant" elements
    checkConstantExpression(node) {
        let self = this;
        if (!(node.getType() === node_1.TwingNodeType.EXPRESSION_CONSTANT || node.getType() === node_1.TwingNodeType.EXPRESSION_ARRAY || node.getType() === node_1.TwingNodeType.EXPRESSION_UNARY_NEG || node.getType() === node_1.TwingNodeType.EXPRESSION_UNARY_POS)) {
            return false;
        }
        for (let [k, n] of node.getNodes()) {
            if (!self.checkConstantExpression(n)) {
                return false;
            }
        }
        return true;
    }
    isUnary(token) {
        return token.test(token_1.TwingToken.OPERATOR_TYPE) && this.unaryOperators.has(token.getValue());
    }
    isBinary(token) {
        return token.test(token_1.TwingToken.OPERATOR_TYPE) && this.binaryOperators.has(token.getValue());
    }
    getTest(line) {
        let stream = this.parser.getStream();
        let name = stream.expect(token_1.TwingToken.NAME_TYPE).getValue();
        let test = this.env.getTest(name);
        if (test) {
            return [name, test];
        }
        if (stream.test(token_1.TwingToken.NAME_TYPE)) {
            // try 2-words tests
            name = name + ' ' + this.parser.getCurrentToken().getValue();
            let test = this.env.getTest(name);
            if (test) {
                stream.next();
                return [name, test];
            }
        }
        let e = new syntax_1.TwingErrorSyntax(`Unknown "${name}" test.`, line, stream.getSourceContext());
        e.addSuggestions(name, [...this.env.getTests().keys()]);
        throw e;
    }
    getFunctionExpressionFactory(name, line, column) {
        let function_ = this.env.getFunction(name);
        if (!function_) {
            let e = new syntax_1.TwingErrorSyntax(`Unknown "${name}" function.`, line, this.parser.getStream().getSourceContext());
            e.addSuggestions(name, Array.from(this.env.getFunctions().keys()));
            throw e;
        }
        if (function_.isDeprecated()) {
            let message = `Twing Function "${function_.getName()}" is deprecated`;
            if (typeof function_.getDeprecatedVersion() !== 'boolean') {
                message += ` since version ${function_.getDeprecatedVersion()}`;
            }
            if (function_.getAlternative()) {
                message += `. Use "${function_.getAlternative()}" instead`;
            }
            let src = this.parser.getStream().getSourceContext();
            message += ` in ${src.getPath() ? src.getPath() : src.getName()} at line ${line}.`;
            process.stdout.write(message);
        }
        return function_.getExpressionFactory();
    }
    getFilterExpressionFactory(name, line, column) {
        let filter = this.env.getFilter(name);
        if (!filter) {
            let e = new syntax_1.TwingErrorSyntax(`Unknown "${name}" filter.`, line, this.parser.getStream().getSourceContext());
            e.addSuggestions(name, Array.from(this.env.getFilters().keys()));
            throw e;
        }
        if (filter.isDeprecated()) {
            let message = `Twing Filter "${filter.getName()}" is deprecated`;
            if (typeof filter.getDeprecatedVersion() !== 'boolean') {
                message += ` since version ${filter.getDeprecatedVersion()}`;
            }
            if (filter.getAlternative()) {
                message += `. Use "${filter.getAlternative()}" instead`;
            }
            let src = this.parser.getStream().getSourceContext();
            message += ` in ${src.getPath() ? src.getPath() : src.getName()} at line ${line}.`;
            process.stdout.write(message);
        }
        return filter.getExpressionFactory();
    }
}
exports.TwingExpressionParser = TwingExpressionParser;
// @todo: should be a separate enum
TwingExpressionParser.OPERATOR_LEFT = 'OPERATOR_LEFT';
TwingExpressionParser.OPERATOR_RIGHT = 'OPERATOR_RIGHT';
